import { Col, Form, SelectProps } from 'antd';
import { FormikProps } from 'formik';
import dayjs from 'dayjs';
import { MDatePicker, MSelect, MTextArea } from '@Components/Base';
import { Option, OptionNum } from '@Components/types/options';
import { attendanceRequestActions } from '@Features/EmployeeSelfService/requests/AttendanceRequest/store/attendanceRequestSlice';
import { useAppDispatch } from '@Hooks/index';
import { prepareDatePickerValue } from '@Utils/transformDateTime';
import { getLocationSymbol } from '@Utils/getLocationSymbol';

import { AttendanceRequestFormValues } from '../../types';
import { AttendanceRequestType } from '../../enum/AttendanceRequestType.enum';

interface ConditionalFormDetailsProps {
  requestType: string;
  formik: FormikProps<AttendanceRequestFormValues>;
  workLocationTypes: OptionNum[];
  companies: Option[];
  departmentProjects: OptionNum[];
  deptProjValueName: string | null;
  reasonValueName: string | undefined;
  reasonsType: Option[];
}

const ConditionalFormDetails: React.FC<ConditionalFormDetailsProps> = ({
  formik,
  requestType,
  workLocationTypes,
  companies,
  departmentProjects,
  reasonsType,
  deptProjValueName,
  reasonValueName,
}) => {
  const {
    taReqToCompID: companyTouched,
    wlId: locationTouched,
    deptProjID: deptProjTouched,
    startDate: startDateTouched,
    endDate: endDateTouched,
  } = formik.touched;
  const {
    taReqToCompID: companyError,
    wlId: locationError,
    deptProjID: deptProjError,
    startDate: startDateError,
    endDate: endDateError,
    // 'reason' passed as groupID for AttendaceRequest
    groupID: reasonError,
  } = formik.errors;
  const dispatch = useAppDispatch();

  const handleChangeLocationType: SelectProps['onChange'] = (selectedValue) => {
    formik.setFieldValue('wlId', selectedValue);
    const transformSelectedValue = getLocationSymbol(Number(selectedValue));
    dispatch(
      attendanceRequestActions.setAttendanceLocation(transformSelectedValue)
    );
  };

  switch (requestType) {
    case AttendanceRequestType.ADDITIONAL_PERMANENT_ACCESS:
      return (
        <>
          <Col span={24}>
            <Form.Item
              label="Company"
              validateStatus={companyTouched && companyError ? 'error' : ''}
              help={companyTouched && companyError ? companyError : ''}
            >
              <MSelect
                labeloption="Company"
                options={companies}
                value={formik.values.taReqToCompID}
                onChange={(selectValue) => {
                  formik.setFieldValue('taReqToCompID', selectValue);
                }}
                onBlur={() => {
                  formik.setFieldTouched('taReqToCompID', true);
                }}
                placeholder="Select Company"
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label="Location"
              validateStatus={locationTouched && locationError ? 'error' : ''}
              help={locationTouched && locationError ? locationError : ''}
            >
              <MSelect
                labeloption="Location"
                options={workLocationTypes}
                value={formik.values.wlId}
                onChange={handleChangeLocationType}
                onBlur={() => {
                  formik.setFieldTouched('wlId', true);
                }}
                placeholder="Select Location"
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label="Department/Project"
              validateStatus={deptProjTouched && deptProjError ? 'error' : ''}
              help={deptProjTouched && deptProjError ? deptProjError : ''}
            >
              <MSelect
                labeloption="Department/Project"
                value={deptProjValueName}
                options={departmentProjects}
                onChange={(selectValue) => {
                  formik.setFieldValue('deptProjID', selectValue);
                }}
                onBlur={() => {
                  formik.setFieldTouched('deptProjID', true);
                }}
                placeholder="Select Department or Project"
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="Remarks">
              <MTextArea
                value={formik.values?.remarks}
                onChange={(event) => {
                  formik.setFieldValue('remarks', event.target.value);
                }}
                rows={4}
                showCount
                maxLength={100}
                placeholder="Leave a comment"
              />
            </Form.Item>
          </Col>
        </>
      );
    case AttendanceRequestType.TEMPORARY_ACCESS:
      return (
        <>
          <Col span={24}>
            <Form.Item
              label="Company"
              validateStatus={companyTouched && companyError ? 'error' : ''}
              help={companyTouched && companyError ? companyError : ''}
            >
              <MSelect
                labeloption="Company"
                options={companies}
                value={formik.values.taReqToCompID}
                onChange={(selectValue) => {
                  formik.setFieldValue('taReqToCompID', selectValue);
                }}
                onBlur={() => {
                  formik.setFieldTouched('taReqToCompID', true);
                }}
                placeholder="Select Company"
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label="Location"
              validateStatus={locationTouched && locationError ? 'error' : ''}
              help={locationTouched && locationError ? locationError : ''}
            >
              <MSelect
                labeloption="Location"
                options={workLocationTypes}
                value={formik.values.wlId}
                onChange={handleChangeLocationType}
                onBlur={() => {
                  formik.setFieldTouched('wlId', true);
                }}
                placeholder="Select Location"
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label="Department/Project"
              validateStatus={deptProjTouched && deptProjError ? 'error' : ''}
              help={deptProjTouched && deptProjError ? deptProjError : ''}
            >
              <MSelect
                labeloption="Department/Project"
                value={deptProjValueName}
                options={departmentProjects}
                onChange={(selectValue) => {
                  formik.setFieldValue('deptProjID', selectValue);
                }}
                onBlur={() => {
                  formik.setFieldTouched('deptProjID', true);
                }}
                placeholder="Select Department or Project"
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label="Date"
              validateStatus={startDateTouched && startDateError ? 'error' : ''}
              help={startDateTouched && startDateError ? startDateError : ''}
            >
              <MDatePicker
                className="leave-deatails-datepicker"
                name="startDate"
                value={
                  formik.values.startDate &&
                  dayjs(formik.values.startDate).tz('UTC')
                }
                format="DD.MM.YYYY"
                onChange={(value) => {
                  formik.setFieldValue(
                    'startDate',
                    prepareDatePickerValue(value, true)
                  );
                }}
                onBlur={() => {
                  formik.setFieldTouched('startDate', true);
                }}
                placeholder="Choose Date"
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label="To Date"
              validateStatus={endDateTouched && endDateError ? 'error' : ''}
              help={endDateTouched && endDateError ? endDateError : ''}
            >
              <MDatePicker
                className="leave-deatails-datepicker"
                name="endDate"
                value={
                  formik.values.endDate &&
                  dayjs(formik.values.endDate).tz('UTC')
                }
                format="DD.MM.YYYY"
                onChange={(value) => {
                  formik.setFieldValue(
                    'endDate',
                    prepareDatePickerValue(value, true)
                  );
                }}
                onBlur={() => {
                  formik.setFieldTouched('endDate', true);
                }}
                placeholder="Choose Date"
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="Remarks">
              <MTextArea
                value={formik.values?.remarks}
                onChange={(event) => {
                  formik.setFieldValue('remarks', event.target.value);
                }}
                rows={4}
                showCount
                maxLength={100}
                placeholder="Leave a comment"
              />
            </Form.Item>
          </Col>
        </>
      );
    case AttendanceRequestType.NO_PUNCH:
    case AttendanceRequestType.LATE_PUNCH:
      return (
        <>
          <Col span={24}>
            <Form.Item
              label="Date"
              validateStatus={startDateTouched && startDateError ? 'error' : ''}
              help={startDateTouched && startDateError ? startDateError : ''}
            >
              <MDatePicker
                className="leave-deatails-datepicker"
                name="startDate"
                value={
                  formik.values.startDate &&
                  dayjs(formik.values.startDate).tz('UTC')
                }
                format="DD.MM.YYYY"
                onChange={(value) => {
                  formik.setFieldValue(
                    'startDate',
                    prepareDatePickerValue(value, true)
                  );
                }}
                onBlur={() => {
                  formik.setFieldTouched('startDate', true);
                }}
                placeholder="Choose Date"
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label="Reason"
              validateStatus={reasonError ? 'error' : ''}
              help={reasonError}
            >
              <MSelect
                labeloption="Reason"
                value={reasonValueName}
                options={reasonsType}
                onChange={(selectValue) => {
                  formik.setFieldValue('groupID', selectValue);
                }}
                placeholder="Select Reason"
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="Remarks">
              <MTextArea
                value={formik.values?.remarks}
                onChange={(event) => {
                  formik.setFieldValue('remarks', event.target.value);
                }}
                rows={4}
                showCount
                maxLength={100}
                placeholder="Leave a comment"
              />
            </Form.Item>
          </Col>
        </>
      );
    case AttendanceRequestType.TECHNICAL_ISSUE:
      return (
        <>
          <Col span={24}>
            <Form.Item
              label="Company"
              validateStatus={companyTouched && companyError ? 'error' : ''}
              help={companyTouched && companyError ? companyError : ''}
            >
              <MSelect
                labeloption="Company"
                options={companies}
                value={formik.values.taReqToCompID}
                onChange={(selectValue) => {
                  formik.setFieldValue('taReqToCompID', selectValue);
                }}
                onBlur={() => {
                  formik.setFieldTouched('taReqToCompID', true);
                }}
                placeholder="Select Company"
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label="Location"
              validateStatus={locationTouched && locationError ? 'error' : ''}
              help={locationTouched && locationError ? locationError : ''}
            >
              <MSelect
                labeloption="Location"
                options={workLocationTypes}
                value={formik.values.wlId}
                onChange={handleChangeLocationType}
                onBlur={() => {
                  formik.setFieldTouched('wlId', true);
                }}
                placeholder="Select Location"
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label="Department/Project"
              validateStatus={deptProjTouched && deptProjError ? 'error' : ''}
              help={deptProjTouched && deptProjError ? deptProjError : ''}
            >
              <MSelect
                labeloption="Department/Project"
                value={deptProjValueName}
                options={departmentProjects}
                onChange={(selectValue) => {
                  formik.setFieldValue('deptProjID', selectValue);
                }}
                onBlur={() => {
                  formik.setFieldTouched('deptProjID', true);
                }}
                placeholder="Select Department or Project"
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label="Date"
              validateStatus={startDateTouched && startDateError ? 'error' : ''}
              help={startDateTouched && startDateError ? startDateError : ''}
            >
              <MDatePicker
                className="leave-deatails-datepicker"
                name="startDate"
                value={
                  formik.values.startDate &&
                  dayjs(formik.values.startDate).tz('UTC')
                }
                format="DD.MM.YYYY"
                onChange={(value) => {
                  formik.setFieldValue(
                    'startDate',
                    prepareDatePickerValue(value, true)
                  );
                }}
                onBlur={() => {
                  formik.setFieldTouched('startDate', true);
                }}
                placeholder="Choose Date"
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label="Reason"
              validateStatus={reasonError ? 'error' : ''}
              help={reasonError}
            >
              <MSelect
                labeloption="Reason"
                value={reasonValueName}
                options={reasonsType}
                onChange={(selectValue) => {
                  formik.setFieldValue('groupID', selectValue);
                }}
                placeholder="Select Reason"
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="Remarks">
              <MTextArea
                value={formik.values?.remarks}
                onChange={(event) => {
                  formik.setFieldValue('remarks', event.target.value);
                }}
                rows={4}
                showCount
                maxLength={100}
                placeholder="Leave a comment"
              />
            </Form.Item>
          </Col>
        </>
      );
    default:
      // covers attendance request types added to the TARequestType dictionary
      // without a dedicated case above (e.g. External Meeting, Training)
      return (
        <>
          <Col span={24}>
            <Form.Item
              label="Date"
              validateStatus={startDateTouched && startDateError ? 'error' : ''}
              help={startDateTouched && startDateError ? startDateError : ''}
            >
              <MDatePicker
                className="leave-deatails-datepicker"
                name="startDate"
                value={
                  formik.values.startDate &&
                  dayjs(formik.values.startDate).tz('UTC')
                }
                format="DD.MM.YYYY"
                onChange={(value) => {
                  formik.setFieldValue(
                    'startDate',
                    prepareDatePickerValue(value, true)
                  );
                }}
                onBlur={() => {
                  formik.setFieldTouched('startDate', true);
                }}
                placeholder="Choose Date"
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="Remarks">
              <MTextArea
                value={formik.values?.remarks}
                onChange={(event) => {
                  formik.setFieldValue('remarks', event.target.value);
                }}
                rows={4}
                showCount
                maxLength={100}
                placeholder="Leave a comment"
              />
            </Form.Item>
          </Col>
        </>
      );
  }
};

export default ConditionalFormDetails;
